import {assert} from 'Asserts';
import {
  isBenchlingTypeDefComplex,
  isBenchlingTypeLeafType,
  LeafType,
  LeafTypeToFriendlyName,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {LookupStepInfo} from 'lookup-config/LookupConfigModalConstants';
import {inferTypeFromLookupStepInfo} from 'lookup-config/LookupConfigUtils';
import {hasAllowedOperatorsForInferredType} from 'processes/modals/router-config/getAllowedOperatorAndValueInfo';
import {isSupportedRouterLookupType} from 'processes/modals/router-config/RouterConfigConstants';
import {Schema} from 'schemas/SchemaConstants';

export function getInvalidLookupForRoutingErrorMessage(
  inferredType: InferredLookupConfigType
): string | null {
  const defaultErrorMessage = 'The selected field cannot be used for router configuration.';
  const {isMultiplex, typeDef} = inferredType;

  if (!isBenchlingTypeLeafType(typeDef) || !isSupportedRouterLookupType(typeDef)) {
    if (isBenchlingTypeDefComplex(typeDef)) {
      return `A ${typeDef.getOperatorName().toLowerCase()} field cannot be used for router configuration.`;
    } else if (isBenchlingTypeLeafType(typeDef)) {
      return `A ${LeafTypeToFriendlyName[
        typeDef
      ].toLowerCase()} field cannot be used for router configuration.`;
    } else {
      return defaultErrorMessage;
    }
  }

  if (!hasAllowedOperatorsForInferredType(inferredType)) {
    return isMultiplex
      ? 'The selected field has multiple values and cannot be used for router configuration.'
      : defaultErrorMessage;
  }

  if (inferredType.isSnapshot) {
    return 'Snapshot fields cannot be used for router configuration.';
  }

  return null;
}

export function isLookupInvalidForRouting(
  lookupStepInfos: Array<LookupStepInfo>,
  baseSchema: Schema | null
): boolean {
  // Some lookups can have null base schemas, which is why it is nullable in most lookup object code and in the parameters.
  assert(baseSchema != null, 'Expected base schema to be defined for flowchart lookup');
  const stepTypes = lookupStepInfos.map((stepInfo) => inferTypeFromLookupStepInfo(stepInfo));
  const lastStepType = stepTypes[stepTypes.length - 1]!;
  const inferredType = {
    typeDef: lastStepType.typeDef,
    linkSchema: lastStepType.linkSchema,
    ...(lastStepType.linkSelectorId != null ? {linkSelectorId: lastStepType.linkSelectorId} : {}),
    isMultiplex: stepTypes.some((stepType) => stepType && stepType.isStepMultiplex),
    isSnapshot: stepTypes.some((stepType) => stepType && stepType.isStepSnapshot),
  };

  if (isBenchlingTypeLeafType(inferredType.typeDef)) {
    if (inferredType.typeDef === LeafType.DROPDOWN_OPTION) {
      return inferredType.linkSelectorId == null;
    }

    if (isSupportedRouterLookupType(inferredType.typeDef)) {
      return false;
    }
  }

  if (isBenchlingTypeDefComplex(inferredType.typeDef) || inferredType.linkSchema == null) {
    return true;
  }

  if (inferredType.isSnapshot) {
    return true;
  }

  return false;
}
